import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import React, { Dispatch, FC, SetStateAction, useContext, useEffect, useMemo, useState } from "react";

import { cleanParams } from "@/components/ui/Form/Filters/FilterUtils";
import { SearchContext } from "@/context/SearchContext";
import useMediaQuery from "@/hooks/useMediaQuery";

interface Props {
  setDisplayFilters: Dispatch<SetStateAction<string | boolean>>;
  resetHandler?: () => void;
  submitHandler?: () => void;
  submitLabel?: string;
  fullWidth?: boolean;
  submitDisabled?: boolean;
  filterName?: string;
  resetSearchFilters?: () => void;
  submitLabelDatalayer?: () => void;
}
const FilterAction: FC<Props> = ({
  setDisplayFilters,
  resetHandler,
  submitHandler,
  submitLabel,
  submitDisabled = false,
  fullWidth = false,
  filterName,
  resetSearchFilters = () => {},
  submitLabelDatalayer = () => {},
}) => {
  const t = useTranslations();
  const { counterParams } = useContext(SearchContext);
  const isMobile = useMediaQuery("(max-width: 600px)");
  const searchParams = useSearchParams();
  const activeFilterCount = useMemo(() => {
    let count = 0;
    searchParams?.forEach((value, key) => {
      if (key === "category" && value) {
        count++;
      }
    });
    return count > 0;
  }, [searchParams]);
  const [isDesktop, setIsDesktop] = useState(false);
  const hasParams = useMemo(() => {
    const cleanedCounterParams = cleanParams(counterParams);
    // delete cleanedCounterParams?.type;
    return Object.keys(cleanedCounterParams).length > 0;
  }, [counterParams]);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 550); // Adjust breakpoint as needed (550px for tablets and above)
    };

    handleResize(); // Check on initial render
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div className={`mt-xs @container/filter-actions ${fullWidth ? "" : "max-w-[290px]"} mx-auto w-full`}>
      <div
        className={`flex flex-col ${isMobile ? "@[500px]/filter-actions:flex-row" : "@[200px]/filter-actions:flex-row"} @[200px]/filter-actions:items-center @[200px]/filter-actions:justify-between gap-[24px]`}
      >
        <div className="basis-1/2 text-right">
          <button
            className="p-xs btn sm:uppercase text-nowrap hover:underline md:hover:bg-brown md:btn-primary-black md:btn-medium md:border-solid md:uppercase"
            type="reset"
            disabled={filterName === "categories" ? !activeFilterCount : !hasParams}
            onClick={() => {
              resetHandler && resetHandler();
              setTimeout(() => {
                setDisplayFilters("");
                setDisplayFilters(false);
              }, 100);
              resetSearchFilters();
            }}
            style={
              isDesktop
                ? {
                    fontWeight: !hasParams ? "500" : undefined,
                    border: "1px solid rgb(50 50 50)",
                    borderRadius: "5rem",
                    backgroundColor: "transparent",
                    color: "rgb(50 50 50)",
                    padding: isMobile ? "" : "10px 20px",
                    fontSize: isMobile ? "" : "11px",
                  }
                : undefined
            }
          >
            {t("button.label.clearAll")}
          </button>
        </div>
        <div className="basis-1/2 text-left">
          <button
            onClick={() => {
              submitHandler && submitHandler();
              setTimeout(() => {
                setDisplayFilters("");
                setDisplayFilters(false);
              }, 100);
              submitLabelDatalayer();
            }}
            disabled={submitDisabled}
            className="btn btn-primary-black btn-medium text-nowrap"
            type="submit"
            style={{ padding: "10px 20px", fontSize: "11px" }}
          >
            {submitLabel ?? t("button.label.done")}
          </button>
        </div>
      </div>
    </div>
  );
};
export default FilterAction;
